import type { UserType } from 'src/auth/types';

import { ANALYTICS_GROUP_TYPE_COMPANY } from './constants';
import { posthogAnalyticsProvider } from './providers/posthog';
import type { AnalyticsEventName, AnalyticsEventPayloadMap } from './events';
import type { AnalyticsEventProperties, AnalyticsProvider } from './types';

const providers: AnalyticsProvider[] = [posthogAnalyticsProvider];

let initialized = false;

function forEachProvider(run: (provider: AnalyticsProvider) => void) {
  for (const provider of providers) {
    try {
      run(provider);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[analytics] ${provider.name} failed:`, error);
      }
    }
  }
}

/** Register an additional provider (e.g. Segment, Amplitude). Called from app bootstrap if needed. */
export function registerAnalyticsProvider(provider: AnalyticsProvider) {
  if (providers.some((p) => p.name === provider.name)) {
    return;
  }
  providers.push(provider);
  if (initialized && provider.init) {
    provider.init();
  }
}

export function initAnalytics() {
  if (initialized) {
    return;
  }
  initialized = true;
  forEachProvider((provider) => provider.init?.());
}

export function trackEvent<E extends AnalyticsEventName>(
  event: E,
  ...args: AnalyticsEventPayloadMap[E] extends void
    ? [properties?: undefined]
    : [properties: AnalyticsEventPayloadMap[E]]
): void {
  const properties = args[0] as AnalyticsEventProperties | undefined;
  forEachProvider((provider) => provider.trackEvent(event, properties));
}

export function captureException(error: unknown, context?: AnalyticsEventProperties) {
  forEachProvider((provider) => provider.captureException?.(error, context));
}

export function syncAnalyticsUser(user: UserType) {
  if (!user) {
    return;
  }

  const distinctId = user.email ?? (user.id != null ? String(user.id) : undefined);
  if (!distinctId) {
    return;
  }

  const traits: AnalyticsEventProperties = {
    email: user.email,
    name: user.displayName ?? user.name,
  };

  forEachProvider((provider) => provider.identifyUser(distinctId, traits));

  const client = user.client;
  if (client?.id == null) {
    return;
  }

  const groupProperties: AnalyticsEventProperties = {
    ...(client.name ? { name: client.name } : {}),
    ...(client.organisationType ? { organisation_type: client.organisationType } : {}),
  };

  forEachProvider((provider) =>
    provider.setGroup?.(ANALYTICS_GROUP_TYPE_COMPANY, String(client.id), groupProperties)
  );
}

export function resetAnalyticsSession() {
  forEachProvider((provider) => provider.resetSession?.());
}
